// Pure helpers for the case add/edit form (CaseList + CaseFormModal).
// A unit only says what the entered number means: switching it never rewrites the number,
// conversions happen only for display and totals.

export const EMPTY_CASE_FORM = {
  name: '',
  description: '',
  notes: '',
  weight: '',
  weight_unit: null,
  outer_length: '',
  outer_width: '',
  outer_height: '',
  outer_dimension_unit: null,
  inner_length: '',
  inner_width: '',
  inner_height: '',
  inner_dimension_unit: null,
  quantity_total: 0,
  quantity_available: 0,
  items_per_case: 1,
};

export const DIMENSION_FIELDS = ['outer_length', 'outer_width', 'outer_height', 'inner_length', 'inner_width', 'inner_height'];

export const toUnitOption = (id, symbol) => (id ? { value: id, label: symbol || String(id) } : null);

// Default unit: preferred symbol (kg / cm), else the default unit from Settings.
export const pickUnitOption = (units, symbol) => {
  const list = Array.isArray(units) ? units : [];
  const unit = list.find((u) => u.symbol === symbol && u.is_active) || list.find((u) => u.is_default);
  return unit ? toUnitOption(unit.id, unit.symbol) : null;
};

const parseDimension = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? null : parsed;
};

/** Form for a new case, pre-filled with kg and cm. */
export const newCaseForm = (weightUnits, dimensionUnits) => {
  const dimensionUnit = pickUnitOption(dimensionUnits, 'cm');
  return {
    ...EMPTY_CASE_FORM,
    weight_unit: pickUnitOption(weightUnits, 'kg'),
    outer_dimension_unit: dimensionUnit,
    inner_dimension_unit: dimensionUnit,
  };
};

/** Form for an existing case; a missing unit falls back to kg / cm so numbers are never saved without one. */
export const editCaseForm = (itemCase, weightUnits, dimensionUnits) => {
  const defaultDimensionUnit = pickUnitOption(dimensionUnits, 'cm');
  return {
    name: itemCase.name,
    description: itemCase.description || '',
    notes: itemCase.notes || '',
    weight: itemCase.weight || '',
    weight_unit: toUnitOption(itemCase.weight_unit, itemCase.weight_unit_symbol) || pickUnitOption(weightUnits, 'kg'),
    ...Object.fromEntries(DIMENSION_FIELDS.map((field) => [field, itemCase[field] ?? ''])),
    outer_dimension_unit:
      toUnitOption(itemCase.outer_dimension_unit, itemCase.outer_dimension_unit_symbol) || defaultDimensionUnit,
    inner_dimension_unit:
      toUnitOption(itemCase.inner_dimension_unit, itemCase.inner_dimension_unit_symbol) || defaultDimensionUnit,
    quantity_total: itemCase.quantity_total ?? 0,
    quantity_available: itemCase.quantity_available ?? 0,
    items_per_case: itemCase.items_per_case ?? 1,
  };
};

/** Switch a unit (weight_unit / outer_dimension_unit / inner_dimension_unit) and keep the entered numbers as they are. */
export const changeCaseUnit = (formData, field, option) => ({ ...formData, [field]: option });

/** API payload for POST/PATCH item-cases. */
export const buildCasePayload = (formData, selectedStockItem) => ({
  ...formData,
  ...Object.fromEntries(DIMENSION_FIELDS.map((field) => [field, parseDimension(formData[field])])),
  weight_unit: formData.weight_unit?.value ?? null,
  outer_dimension_unit: formData.outer_dimension_unit?.value ?? null,
  inner_dimension_unit: formData.inner_dimension_unit?.value ?? null,
  weight: parseFloat(formData.weight) || 0,
  quantity_total: parseInt(formData.quantity_total) || 0,
  quantity_available: parseInt(formData.quantity_available) || 0,
  items_per_case: parseInt(formData.items_per_case) || 1,
  stock_item: selectedStockItem ? selectedStockItem.value : null,
});
